import { create } from 'zustand';
import type {
  SupportedCurrency,
  TimeFormatType,
  NumberFormatType,
  DateFormatType,
  LocalizationPreferences,
} from '@/core/types/localization.types';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface LocalizationState extends LocalizationPreferences {
  hydrated: boolean;
  setCurrency: (currency: SupportedCurrency) => void;
  setTimezone: (timezone: string) => void;
  setDateFormat: (dateFormat: DateFormatType) => void;
  setTimeFormat: (timeFormat: TimeFormatType) => void;
  setNumberFormat: (numberFormat: NumberFormatType) => void;
  setAll: (prefs: Partial<LocalizationPreferences>) => void;
}

/* ------------------------------------------------------------------ */
/*  IndexedDB persistence — reuses the same DB as theme store          */
/* ------------------------------------------------------------------ */

const DB_NAME = 'hms-settings';
const STORE_NAME = 'preferences';
const L10N_KEY = 'localization';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readFromDB(): Promise<LocalizationPreferences | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(L10N_KEY);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function writeToDB(prefs: LocalizationPreferences): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(prefs, L10N_KEY);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // Persistence is best-effort
  }
}

/* ------------------------------------------------------------------ */
/*  Helper to persist current prefs after a state change               */
/* ------------------------------------------------------------------ */

function persistState() {
  const { hydrated: _, setCurrency: _a, setTimezone: _b, setDateFormat: _c, setTimeFormat: _d, setNumberFormat: _e, setAll: _f, ...prefs } = useLocalizationStore.getState();
  writeToDB(prefs as LocalizationPreferences);
}

/* ------------------------------------------------------------------ */
/*  Defaults — USD + UTC as specified                                  */
/* ------------------------------------------------------------------ */

const DEFAULTS: LocalizationPreferences = {
  currency: 'USD',
  timezone: 'UTC',
  dateFormat: 'DD MMM YYYY',
  timeFormat: '12',
  numberFormat: 'international',
};

/* ------------------------------------------------------------------ */
/*  Zustand store                                                      */
/* ------------------------------------------------------------------ */

export const useLocalizationStore = create<LocalizationState>((set) => ({
  ...DEFAULTS,
  hydrated: false,

  setCurrency: (currency) => {
    set({ currency });
    persistState();
  },
  setTimezone: (timezone) => {
    set({ timezone });
    persistState();
  },
  setDateFormat: (dateFormat) => {
    set({ dateFormat });
    persistState();
  },
  setTimeFormat: (timeFormat) => {
    set({ timeFormat });
    persistState();
  },
  setNumberFormat: (numberFormat) => {
    set({ numberFormat });
    persistState();
  },
  setAll: (prefs) => {
    set(prefs);
    persistState();
  },
}));

/* ------------------------------------------------------------------ */
/*  Hydration — restore persisted prefs once on client load            */
/* ------------------------------------------------------------------ */

if (typeof window !== 'undefined') {
  readFromDB().then((persisted) => {
    if (persisted) {
      useLocalizationStore.setState({ ...persisted, hydrated: true });
    } else {
      useLocalizationStore.setState({ hydrated: true });
    }
  });
}
